import { z } from 'zod';
import { DayOfWeek, dayOfWeekSchema, meetingBlockLikeSchema } from './meeting-block.js';
import { minimalScheduleSchema, scheduleSchema } from './schedule.js';
import { coachPiiType, studentPiiType } from './pii-schema.js';
import { userLikeSchema } from './user-like.js';

import { Temporal } from 'temporal-polyfill';
import { formatPlainTimeMinutePrecision } from '../temporal-utils/formatting.js';
import { ClassTimeType, classTimeTypeSchema, MinimalClassTime, minimalClassTimeSchema } from './class-time-shared.js';
import type { MinimalAvailability } from './coach-availability-shared.js';
import { adjustAvailabilityByTimeZone } from '../coach-availability-utils.js';
import { startOfWeekPlainDate } from '../day-of-week-utils.js';
import { coachOverallGradeSchema } from './coach-overall-grade.js';
export { ClassTimeType } from './class-time-shared.js';
export type { MinimalClassTime } from './class-time-shared.js';
export { classTimeTypeSchema, minimalClassTimeSchema };

export const classTimeStatusSchema = z.enum(['active', 'archived', 'deleted']);

export const classTimeTypeLabelMap: Record<ClassTimeType, string> = {
  recurring: 'Recurring',
  one_time: 'One-time',
};

/**
 * Stable string key for a wall-clock start/end pair (minute-precision plain times, pipe-separated).
 * Use for option values, lookup, and deduping CT-adjusted class time rows — not for user-facing display.
 */
export function encodeClassTimeMeetingTimePair(
  startPlainTime: Temporal.PlainTime,
  endPlainTime: Temporal.PlainTime,
): string {
  return `${formatPlainTimeMinutePrecision(startPlainTime)}|${formatPlainTimeMinutePrecision(endPlainTime)}`;
}

export const groupedMeetingBlockSchema = z.object({
  timeInterval: z.object({
    startPlainTime: z.instanceof(Temporal.PlainTime),
    endPlainTime: z.instanceof(Temporal.PlainTime),
  }),
  days: z.array(dayOfWeekSchema),
});

export const classTimeSchema = minimalClassTimeSchema.extend({
  schedule: z.lazy(() => minimalScheduleSchema),
});

export type ClassTime = z.infer<typeof classTimeSchema>;

export const fullClassTimeSchema = minimalClassTimeSchema.extend({
  schedule: z.lazy(() => scheduleSchema),
  groupedMeetingBlocks: z.array(groupedMeetingBlockSchema),
});
export type FullClassTime = z.infer<typeof fullClassTimeSchema>;

export const classTimeWithStudentsSchema = classTimeSchema.extend({
  schedule: z.lazy(() => minimalScheduleSchema),
  students: z.array(
    z.object({
      studentGroupId: z.string().nullable(),
      students: z.array(z.lazy(() => userLikeSchema.extend(studentPiiType.shape))),
    }),
  ),
  availableCoaches: z
    .array(
      z
        .object({
          id: z.string(),
          fullNameOrEmail: z.string().nullable(),
          overallGrade: coachOverallGradeSchema,
          conflictType: z.string().nullable(),
          conflictAmount: z.number().nullable(),
          oneTimeConflictCount: z.number().optional(),
        })
        .extend(coachPiiType.shape),
    )
    .optional(),
  canArchive: z.boolean(),
});

export type ClassTimeWithStudents = z.infer<typeof classTimeWithStudentsSchema>;

export const classTimeScheduleRowSchema = minimalClassTimeSchema.extend({
  studentCount: z.number(),
  canArchive: z.boolean(),
  availableCoaches: z
    .array(
      z
        .object({
          id: z.string(),
          fullNameOrEmail: z.string().nullable(),
          overallGrade: coachOverallGradeSchema.nullable(),
          conflictType: z.string().nullable(),
          conflictAmount: z.number().nullable(),
          hasGoogleMeetLink: z.boolean().optional(),
          oneTimeConflictCount: z.number().optional(),
        })
        .extend(coachPiiType.shape),
    )
    .optional(),
});

export type ClassTimeScheduleRow = z.infer<typeof classTimeScheduleRowSchema>;

export const createClassTimeOptionsSchema = z.object({
  type: classTimeTypeSchema,
  scheduleId: z.string(),
  meetingBlocks: z.array(z.lazy(() => meetingBlockLikeSchema)),
  studentSeats: z.number().int().nonnegative().optional().default(0),
});
export type CreateClassTimeOptions = z.input<typeof createClassTimeOptionsSchema>;

export const updateClassTimeOptionsSchema = z.object({
  type: classTimeTypeSchema,
  meetingBlocks: z.array(z.lazy(() => meetingBlockLikeSchema)),
  studentSeats: z.number().int().nonnegative().optional(),
});
export type UpdateClassTimeOptions = z.infer<typeof updateClassTimeOptionsSchema>;

export const adjustMinimalClassTimeAcrossTZ = <T extends MinimalClassTime>(
  classTime: T,
  fromTimeZone: string,
  toTimeZone: string,
  referencePlainDate?: Temporal.PlainDate,
): T => {
  return {
    ...classTime,
    meetingBlocks: classTime.meetingBlocks.map((meetingBlock) => {
      // If the timezone requires a day shift, we need to adjust the day
      const availability: MinimalAvailability = {
        day: meetingBlock.day,
        startPlainTime: meetingBlock.startPlainTime,
        endPlainTime: meetingBlock.endPlainTime,
      };

      const { startAdjustedPlainTime, endAdjustedPlainTime, adjustedDay } = adjustAvailabilityByTimeZone(availability, {
        sourceTimeZone: fromTimeZone,
        targetTimeZone: toTimeZone,
        referencePlainDate,
      });

      return {
        ...meetingBlock,
        day: adjustedDay,
        startPlainTime: startAdjustedPlainTime,
        endPlainTime: endAdjustedPlainTime,
      };
    }),
  };
};

/**
 * Converts a class time for a weekly summary such as "Mon, Thu, 9:00 - 9:30".
 * Recurring class times convert every weekday within the current Monday–Sunday week in `fromTimeZone`,
 * so all days share one calendar week (and the DST offsets of that week). One-time class times keep the
 * default per-day conversion.
 */
export const adjustClassTimeForWeeklyDisplay = <T extends MinimalClassTime>(
  classTime: T,
  fromTimeZone: string,
  toTimeZone: string,
  now: Temporal.Instant = Temporal.Now.instant(),
): T => {
  const referencePlainDate =
    classTime.type === ClassTimeType.recurring
      ? startOfWeekPlainDate(now.toZonedDateTimeISO(fromTimeZone).toPlainDate())
      : undefined;

  return adjustMinimalClassTimeAcrossTZ(classTime, fromTimeZone, toTimeZone, referencePlainDate);
};

export const adjustGroupedMeetingBlocksAcrossTZ = (
  groupedBlocks: {
    timeInterval: { startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime };
    days: DayOfWeek[];
  }[],
  fromTimeZone: string,
  toTimeZone: string,
) => {
  return groupedBlocks.map((block) => {
    const adjustedPerDay = block.days.map((day) =>
      adjustAvailabilityByTimeZone(
        {
          day,
          startPlainTime: block.timeInterval.startPlainTime,
          endPlainTime: block.timeInterval.endPlainTime,
        },
        {
          sourceTimeZone: fromTimeZone,
          targetTimeZone: toTimeZone,
        },
      ),
    );

    if (adjustedPerDay.length === 0) {
      return block;
    }

    const [firstAdjusted] = adjustedPerDay;

    return {
      ...block,
      timeInterval: {
        startPlainTime: firstAdjusted.startAdjustedPlainTime,
        endPlainTime: firstAdjusted.endAdjustedPlainTime,
      },
      days: adjustedPerDay.map(({ adjustedDay }) => adjustedDay),
    };
  });
};

export const classTimeCoachCountSchema = z.object({
  class_time_id: z.string(),
  count: z.bigint(),
});
export type ClassTimeCoachCount = z.infer<typeof classTimeCoachCountSchema>;

export const classTimeCoachesSchema = z.array(
  z.object({
    classTimeId: z.string(),
    coaches: z.array(
      z.object({
        id: z.string(),
        fullName: z.string(),
        overallGrade: coachOverallGradeSchema,
      }),
    ),
  }),
);
export type ClassTimeCoaches = z.infer<typeof classTimeCoachesSchema>;
